import { modulesApi, ModuleExecutionRequest } from '../views/ModulesView/api/modulesApi';
import { config } from '../config';

/**
 * PWA Module Service — exposes the module registry to the UI.
 *
 * Provides:
 * - executeModule(moduleId, params, workflowId?) — trigger a module by ID
 * - getTaskStatus(taskId) — check async task status from localStorage
 * - listModules() — return all registered module IDs and metadata
 */

const STORAGE_KEY = 'module_task_status';

interface TaskStatus {
  taskId: string;
  moduleId: string;
  status: 'idle' | 'running' | 'completed' | 'failed';
  progress: number;
  result?: any;
  error?: string;
  timestamp: string;
}

function loadTasks(): Record<string, TaskStatus> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveTasks(tasks: Record<string, TaskStatus>) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(tasks));
}

function upsertTask(task: TaskStatus) {
  const tasks = loadTasks();
  tasks[task.taskId] = task;
  saveTasks(tasks);
}

export const moduleService = {
  /**
   * Execute a module by ID with given params.
   * Tracks task status in localStorage for persistence across reloads.
   *
   * @param moduleId — the module key (must match KiloBridge registry, e.g. 'domain_manager')
   * @param params — action-specific payload, must include { action } key
   * @param workflowId — optional workflow ID to look up in the Workflows sheet
   */
  async executeModule(moduleId: string, params?: any, workflowId?: string): Promise<any> {
    const taskId = `${moduleId}_${Date.now()}`;
    upsertTask({
      taskId,
      moduleId,
      status: 'running',
      progress: 0,
      timestamp: new Date().toISOString(),
    });

    try {
      const clientId = localStorage.getItem('userId') || 'unknown_client';
      const request: ModuleExecutionRequest = {
        clientId,
        moduleId: moduleId as any,
        workflowId,
        params: params ?? {},
        priority: 'standard',
      };

      const response = await modulesApi.executeModule(request);
      const executionId = response.executionId;

      upsertTask({
        taskId,
        moduleId,
        status: 'completed',
        progress: 100,
        result: { executionId },
        timestamp: new Date().toISOString(),
      });

      // Return the full API response so callers can access result data
      return response;
    } catch (err: any) {
      upsertTask({
        taskId,
        moduleId,
        status: 'failed',
        progress: 0,
        error: err?.message ?? 'Unknown error',
        timestamp: new Date().toISOString(),
      });
      throw err;
    }
  },

  /**
   * Get the latest task status for a given module ID.
   */
  getLatestTask(moduleId: string): TaskStatus | null {
    const tasks = loadTasks();
    const matching = Object.values(tasks)
      .filter(t => t.moduleId === moduleId)
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    return matching[0] ?? null;
  },

  /**
   * Get all task statuses.
   */
  getAllTasks(): TaskStatus[] {
    return Object.values(loadTasks()).sort(
      (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );
  },

  /**
   * Clear all stored task history.
   */
  clearTasks() {
    localStorage.removeItem(STORAGE_KEY);
  },
};
